"use strict";

import * as joint from '@joint/core';
import { Vector3vl } from '3vl';
import * as tools from '../tools.mjs';
import $ from 'jquery';

export const portGroupAttrs = {
    wire: {
        stroke: '#4B4F6A',
        x1: 0, y1: 0,
        x2: undefined, y2: 0
    },
    port: {
        magnet: undefined,
        r: 7,
        stroke: 'black',
        fill: 'white',
        strokeWidth: 2,
        strokeOpacity: 0.5
    },
    bits: {
        ref: 'port',
        fill: 'black',
        fontSize: '7pt'
    },
    iolabel: {
        textVerticalAnchor: 'middle',
        fill: 'black',
        fontSize: '8pt'
    },
    decor: {
        stroke: 'black',
        fill: 'transparent'
    }
};

// Common base class for gate models
export const Gate = joint.dia.Element.define('Gate', {
    /* default properties */
    propagation: 1,
    label: '',

    size: { width: 80, height: 30 },
    inputSignals: {},
    outputSignals: {},
    attrs: {
        '.': { magnet: false },
        body: { stroke: 'black', strokeWidth: 2 },
        label: {
            refX: .5, refDy: 3,
            textAnchor: 'middle',
            fontSize: '8pt',
            fill: 'black'
        }
    },
    ports: {
        groups: {
            'in': {
                position: 'left',
                attrs: joint.util.merge({}, portGroupAttrs, {
                    wire: { x2: -25 },
                    port: { magnet: 'passive', refX: -25 },
                    bits: { refDx: 1, refY: -3, textAnchor: 'start' },
                    iolabel: { refX: 5, textAnchor: 'start' }
                })
            },
            'out': {
                position: 'right',
                attrs: joint.util.merge({}, portGroupAttrs, {
                    wire: { x2: 25 },
                    port: { magnet: true, refX: 25 },
                    bits: { refX: -1, refY: -3, textAnchor: 'end' },
                    iolabel: { refX: -5, textAnchor: 'end' }
                })
            }
        }
    },

    z: 1
}, {
    operation() {
        return {};
    },
    initialize() {
        // pre-process ports
        const ports = this.get('ports');
        if (ports.items) {
            this._preprocessPorts(ports.items);
        }
        joint.dia.Element.prototype.initialize.apply(this, arguments);
        this.prepare();

        this.bindAttrToProp('label/text', 'label');
        if (this._unsupportedPropChanges.length > 0) {
            this.on(this._unsupportedPropChanges.map(prop => 'change:'+prop).join(' '), (__, ___, opt) => {
                if (opt.init) return;

                const changed = joint.util.intersection(Object.keys(this.changed), this._unsupportedPropChanges);
                changed.forEach(attr => {
                    this.set(attr, this.previous(attr), {init: true});
                });
                console.warn('Beta property change support: "' + changed + '" changes on ' + this.get('type') + ' are (currently) not supported.');
            });
        }
    },
    prepare() {
    },
    bindAttrToProp(attr, prop) {
        this.attr(attr, this.get(prop));
        this.on('change:' + prop, (_, val) => this.attr(attr, val));
    },

    /*
     * Private methods.
     */
    _changeOutputSignals(sigs) {
        this.graph.getConnectedLinks(this, {outbound: true})
            .forEach(wire => {
                wire.set('signal', sigs[wire.get('source').port]);
            });
    },
    _setInput(sig, port) {
        const signals = joint.util.clone(this.get('inputSignals'));
        signals[port] = sig;
        this.set('inputSignals', signals);
    },
    _clearInput(port) {
        const bits = this.getPort(port).bits;
        this._setInput(Vector3vl.xes(bits), port);
    },
    _preprocessPorts(ports) {
        this._resetPortsSignals(ports);
        for (const port of ports) {
            port.attrs = {};
            port.attrs['bits'] = { text: this._getBitsText(port.bits) }
            if (port.labelled) {
                const iolabel = { text: 'portlabel' in port ? port.portlabel : port.id };
                if (port.polarity === false)
                    iolabel['text-decoration'] = 'overline';
                if (port.decor) {
                    console.assert(port.group == 'in');
                    iolabel['refX'] = 10;
                }
                port.attrs['iolabel'] = iolabel;
            }
            if (port.decor) {
                port.attrs['decor'] = { d: port.decor };
            }
        }
    },
    _setPortsBits(portsBits) {
        const ports = joint.util.cloneDeep(this.get('ports'));
        const portsReset = [];
        for (const portid in portsBits) {
            const bits = portsBits[portid];
            const port = ports.items.find(function(port) {
                return port.id && port.id === portid;
            });
            port.bits = bits;
            port.attrs['bits'].text = this._getBitsText(bits);
            portsReset.push(port);
        }
        this._resetPortsSignals(portsReset);
        //trigger port changes on model and view
        this.set('ports', ports);
        this.graph.getConnectedLinks(this, { outbound: true })
            .filter((wire) => wire.get('source').port in portsBits)
            .forEach((wire) => wire._changeSource(wire.get('source')));
        this.graph.getConnectedLinks(this, { inbound: true })
            .filter((wire) => wire.get('target').port in portsBits)
            .forEach((wire) => wire._changeTarget(wire.get('target')));
    },
    _getBitsText(bits) {
        return bits > 1 ? bits : '';
    },
    _resetPortValue(port) {
        return Vector3vl.xes(port.bits);
    },
    _resetPortsSignals(ports) {
        const signals = {
            in: this.get('inputSignals'),
            out: this.get('outputSignals')
        }

        for (const port of ports) {
            console.assert(port.bits >= 0);
            signals[port.dir][port.id] = this._resetPortValue(port);
        }

        this.set('inputSignals', signals.in);
        this.set('outputSignals', signals.out);
    },
    _removePortsSignals(ports) {
        const signals = {
            in: this.get('inputSignals'),
            out: this.get('outputSignals')
        }

        for (const port of ports) {
            delete signals[port.dir][port.id];
        }

        this.set('inputSignals', signals.in);
        this.set('outputSignals', signals.out);
    },

    /*
     * JointJS port interface with custom hooks to adapt signals.
     */
    addPort(port) {
        this.addPorts([port]);
    },
    addPorts(ports) {
        this._preprocessPorts(ports);
        joint.dia.Element.prototype.addPorts.apply(this, arguments);
    },
    removePort(port, opt) {
        this.removePorts([port]);
    },
    removePorts(ports, opt) {
        this._removePortsSignals(ports);
        joint.dia.Element.prototype.removePorts.apply(this, arguments);
    },

    /*
     * Layout methods.
     */
    _getStackedPosition(opt) {
        return function(portsArgs, elBBox) {
            // ports stacked from top to bottom or left to right
            const side = opt.side || 'left';
            const step = opt.step || 16;
            const offset = opt.offset || 12;
            const x = side == 'left' ? elBBox.topLeft().x : side == 'right' ? elBBox.topRight().x : undefined;
            const y = side == 'top' ? elBBox.topLeft().y : side == 'bottom' ? elBBox.bottomRight().y : undefined;
            if (x !== undefined) {
                return portsArgs.map((portArgs, index) => {
                    index += portArgs.idxOffset || 0;
                    return joint.g.Point({ x: x, y: index*step + offset });
                });
            } else {
                return portsArgs.map((portArgs, index) => {
                    index += portArgs.idxOffset || 0;
                    return joint.g.Point({ x: index*step + offset, y: y });
                });
            }
        }
    },
    getLayoutSize() {
        return this.size();
    },
    setLayoutPosition(position) {
        this.set('position', {
            x: position.x,
            y: position.y
        });
    },

    /*
     * View Markup.
     */
    portMarkup: [{
        tagName: 'line',
        className: 'wire',
        selector: 'wire'
    }, {
        tagName: 'circle',
        className: 'port',
        selector: 'port'
    }, {
        tagName: 'text',
        className: 'bits',
        selector: 'bits'
    }, {
        tagName: 'text',
        className: 'iolabel',
        selector: 'iolabel'
    }, {
        tagName: 'path',
        className: 'decor',
        selector: 'decor'
    }],
    //portLabelMarkup: null, //todo: see https://github.com/clientIO/joint/issues/1278
    markup: [{
        tagName: 'text',
        className: 'label',
        selector: 'label'
    }],

    /*
     * Gate parameters for serialization.
     */
    getGateParams(layout) {
        return joint.util.cloneDeep(joint.util.pick(this.attributes, this._gateParams.concat(layout ? this._gateLayoutParams : [])));
    },
    _gateParams: ['label', 'type', 'propagation', 'source_positions'],
    _gateLayoutParams: ['position'],
    _unsupportedPropChanges: [],
    _operationHelpers: [],
    _presentationParams: [],
    _gateKind: 'combinational'
});

export const GateView = joint.dia.ElementView.extend({
    attrs: {
        signal: {
            high: { port: { 'stroke': '#03c03c' } },
            low: { port: { 'stroke': '#fc7c68' } },
            def: { port: { 'stroke': '#779ecb' } },
            undef: { port: { 'stroke': '#999' } }
        }
    },
    presentationAttributes: joint.dia.ElementView.addPresentationAttributes({
        inputSignals: 'SIGNAL',
        outputSignals: 'SIGNAL2'
    }),
    stopprop(evt) {
        evt.stopPropagation();
    },
    confirmUpdate(flags) {
        if (this.hasFlag(flags, 'SIGNAL')) {
            this._updatePortSignals('in');
        }
        if (this.hasFlag(flags, 'SIGNAL2')) {
            this._updatePortSignals('out');
        }
        joint.dia.ElementView.prototype.confirmUpdate.apply(this, arguments);
    },
    _updatePortSignals(dir) {
        const signals =
            dir === 'in' ? this.model.get('inputSignals') :
            dir === 'out' ? this.model.get('outputSignals') :
            joint.util.merge({}, this.model.get('inputSignals'), this.model.get('outputSignals'));
        for (const port in signals) {
            const signal = signals[port];
            const attrs = this.attrs.signal[
                !signal.isDefined ? 'undef' :
                signal.isHigh ? 'high' :
                signal.isLow ? 'low' : 'def'
            ];
            this._applyPortAttrs(port, attrs);
        }
    },
    _applyAttrs(attrs) {
        for (const selector in attrs) {
            const node = this.selectors[selector];
            this.setNodeAttributes(node, attrs[selector]);
        }
    },
    _applyPortAttrs(port, attrs) {
        if (port in this._portElementsCache) {
            for (const selector in attrs) {
                const node = this._portElementsCache[port].portSelectors[selector];
                this.setNodeAttributes(node, attrs[selector]);
            }
        }
    },
    // overrides joint.dia.ElementView.prototype._updatePorts to reflect port signals
    _updatePorts() {
        joint.dia.ElementView.prototype._updatePorts.apply(this, arguments);
        this._updatePortSignals();
    }
});

// Connecting wire model
export const Wire = joint.shapes.standard.Link.define('Wire', {
    attrs: {
        line: {
            class: 'connection',
            targetMarker: null
        },
        wrapper: {
            stroke: 'red'
        }
    },

    signal: Vector3vl.xes(1),
    bits: 1,
    warning: false,

    // show behind gates
    z: 0
}, {
    initialize() {
        joint.shapes.standard.Link.prototype.initialize.apply(this, arguments);
        if (this.has('netname')) {
            this.label(0, {
                markup: [
                    {
                        tagName: 'text',
                        selector: 'label'
                    }
                ],
                attrs: {
                    label: {
                        text: this.get('netname'),
                        fill: 'black',
                        fontSize: '8pt'
                    }
                },
                position: {
                    distance: 0.5
                }
            });
        }
    },
    onAdd() {
        this._changeSource(this.get('source'));
        this._was_added = true; // hacky fix for warnings calculation
    },
    remove() {
        const tar = this.get('target');
        const target = this.graph.getCell(tar.id);
        if (target && 'port' in tar) {
            target._clearInput(tar.port);
        }
        joint.shapes.standard.Link.prototype.remove.apply(this, arguments);
    },

    /*
     * Private methods.
     */
    _propagateSignal(tar, sig) {
        const target = this.getTargetElement();
        if (target) {
            if (this.get('warning'))
                target._clearInput(tar.port);
            else
                target._setInput(sig, tar.port);
        }
    },
    _changeSignal(sig) {
        this._propagateSignal(this.get('target'), sig);
    },
    _changeSource(src) {
        const source = this.getSourceElement();
        if (source && 'port' in src) {
            this.set('bits', source.getPort(src.port).bits);
            this._checkConnection();
            //set signal after checking connection to avoid false signal propagation
            this.set('signal', source.get('outputSignals')[src.port]);
        } else {
            this.set('signal', Vector3vl.xes(this.get('bits')));
        }
    },
    _changeTarget(tar) {
        this._checkConnection();
        if ('port' in tar) {
            this._propagateSignal(tar, this.get('signal'));
        }
        if (!this.hasChanged('target')) return;
        const preTar = this.previous('target');
        const preTarget = this.graph.getCell(preTar.id);
        if (preTarget && 'port' in preTar) {
            preTarget._clearInput(preTar.port);
        }
    },
    _checkConnection() {
        const tar = this.get('target');
        const target = this.graph.getCell(tar.id);
        this.set('warning', (target && target.getPort(tar.port).bits !== this.get('bits')) || false, {silent: !this._was_added});
    },

    /*
     * Wire parameters for serialization.
     */
    getWireParams(layout) {
        const connector = {
            from: {
                id: this.get('source').id,
                port: this.get('source').port
            },
            to: {
                id: this.get('target').id,
                port: this.get('target').port
            }
        };
        if (this.has('netname'))
            connector.name = this.get('netname');
        if (layout && this.has('vertices') && this.get('vertices').length > 0)
            connector.vertices = joint.util.cloneDeep(this.get('vertices'));
        return connector;
    },
    getWirePath() {
        const hier = [];
        for (let sc = this.graph.get('subcircuit'); sc != null; sc = sc.graph.get('subcircuit')) {
            if (!sc.has('label')) return null;
            hier.push(sc.get('label'));
        }
        return hier.reverse();
    }
});

export const WireView = joint.dia.LinkView.extend({
    initFlag: joint.dia.LinkView.prototype.initFlag.concat(['INIT']),
    longWireLength: 400,
    attrs: {
        signal: {
            high: { line: { 'stroke': '#03c03c' } },
            low: { line: { 'stroke': '#fc7c68' } },
            def: { line: { 'stroke': '#779ecb' } },
            undef: { line: { 'stroke': '#999' } }
        },
        bits: {
            bus: { line: { 'stroke-width': '4px' } },
            single: { line: { 'stroke-width': '2px' } }
        },
        warning: {
            warn: { wrapper: { 'stroke-opacity': '0.5' } },
            none: { wrapper: { 'stroke-opacity': '0' } }
        }
    },
    // only instantiate toolsView in browser-environment
    _wireToolsView: (typeof window !== 'undefined') ? new joint.dia.ToolsView({
        tools: [
            new joint.linkTools.Vertices({ focusOpacity: 0.5 }),
            //new joint.linkTools.Segments({ focusOpacity: 0.5 }), //todo: problem with signal reset ??,
            new tools.CircleSourceArrowhead(),
            new tools.CircleTargetArrowhead(),
            new tools.RemoveButton({ distanceShort: '75%', distanceLong: '50' }),
            new tools.RemoveButton({ distance: '-50', secondary: true }),
            new tools.MonitorButton({ distanceShort: '25%', distanceLong: '30' }),
            new tools.MonitorButton({ distance: '-30', secondary: true })
        ]
    }) : null,
    presentationAttributes: joint.dia.LinkView.addPresentationAttributes({
        signal: 'SIGNAL',
        bits: 'BITS',
        warning: 'WARNING'
    }),
    initialize() {
        joint.dia.LinkView.prototype.initialize.apply(this, arguments);
        this.prevModels = { source: null, target: null };
    },
    confirmUpdate(flags) {
        joint.dia.LinkView.prototype.confirmUpdate.apply(this, arguments);
        if (this.hasFlag(flags, 'SIGNAL')) {
            this._updateSignal();
        }
        if (this.hasFlag(flags, 'BITS')) {
            this._updateBits();
        }
        if (this.hasFlag(flags, 'WARNING')) {
            this._updateWarning();
        }
    },

    isShortWire() {
        return this.getConnectionLength() < this.longWireLength;
    },
    _updateSignal() {
        const signal = this.model.get('signal');
        const attrs = this.attrs.signal[
            !signal.isDefined ? 'undef' :
            signal.isHigh ? 'high' :
            signal.isLow ? 'low' : 'def'
        ];
        this._applyAttrs(attrs);
    },
    _updateBits() {
        const bits = this.model.get('bits');
        const attrs = this.attrs.bits[
            bits > 1 ? 'bus' : 'single'
        ];
        this._applyAttrs(attrs);
    },
    _updateWarning() {
        const warning = this.model.get('warning');
        const attrs = this.attrs.warning[
            warning ? 'warn' : 'none'
        ];
        this._applyAttrs(attrs);
    },
    _applyAttrs(attrs) {
        for (const selector in attrs) {
            const node = this.selectors[selector];
            this.setNodeAttributes(node, attrs[selector]);
        }
    },

    update() {
        joint.dia.LinkView.prototype.update.apply(this, arguments);
        this._updateSignal();
        this._updateBits();
        this._updateWarning();
    },
    onRemove() {
        joint.dia.LinkView.prototype.onRemove.apply(this, arguments);
        this._removeTooltip();
    },

    mouseenter(evt) {
        joint.dia.LinkView.prototype.mouseenter.apply(this, arguments);
        this.addTools(this._wireToolsView);
        this._addTooltip({x: evt.clientX + 5, y: evt.clientY + 5 });
    },
    mouseleave(evt) {
        joint.dia.LinkView.prototype.mouseleave.apply(this, arguments);
        this.removeTools();
        this._removeTooltip();
    },

    /*
     * Display current value on wire in tooltip.
     */
    _addTooltip(pos) {
        if (this.model.get('bits') == 1) return;
        if (this.wire_hover) return;
        this.wire_hover = $('<div class="wire_hover">')
            .css('left', pos.x)
            .css('top', pos.y)
            .appendTo($(document.body));
        this._generateTextForTooltip();
        this.listenTo(this.model, 'change:signal', this._generateTextForTooltip);
    },
    _removeTooltip() {
        if (this.wire_hover) {
            this.wire_hover.remove();
            this.wire_hover = null;
            this.stopListening(this.model, 'change:signal', this._generateTextForTooltip);
        }
    },
    _generateTextForTooltip() {
        if (!this.wire_hover) return;
        const sig = this.model.get('signal');
        const display3vl = this.model.graph._display3vl;
        const hovertext = [
            'Hex: ' + display3vl.show('hex', sig) + '<br>',
            'Dec: ' + display3vl.show('dec', sig) + '<br>',
            'Oct: ' + display3vl.show('oct', sig) + '<br>',
            'Bin: ' + display3vl.show('bin', sig)
        ].join('');
        this.wire_hover.html(hovertext);
    },

    // Quick-and-dirty performance fix
    onEndModelChange(endType, endModel, opt) {
        if (typeof endModel == 'object' && endModel != null &&
            endModel == this.prevModels[endType] &&
            Object.keys(endModel.changed).length > 0 &&
            !('position' in endModel.changed)) return;
        joint.dia.LinkView.prototype.onEndModelChange.apply(this, arguments);
        this.prevModels[endType] = endModel;
    }
});

// base class for gates displayed as a box
export const Box = Gate.define('Box', {
    attrs: {
        body: { refWidth: 1, refHeight: 1 },
        tooltip: { refX: 0, refY: -30, height: 30 }
    }
}, {
    initialize() {
        Gate.prototype.initialize.apply(this, arguments);
        this.on('change:size', (_, size) => {
            if (size.width > this.tooltipMinWidth) {
                this.attr('tooltip', { refWidth: 1, width: null });
            } else {
                this.attr('tooltip', { refWidth: null, width: this.tooltipMinWidth });
            }
        });
        this.trigger('change:size', this, this.prop('size'));
    },
    markup: Gate.prototype.markup.concat([{
            tagName: 'rect',
            className: 'body',
            selector: 'body'
        }
    ]),
    markupZoom: [{
        tagName: 'foreignObject',
        className: 'tooltip',
        selector: 'tooltip',
        children: [{
            tagName: 'body',
            namespaceURI: 'http://www.w3.org/1999/xhtml',
            children: [{
                tagName: 'a',
                className: 'zoom',
                textContent: '🔍',
                style: { cursor: 'pointer' }
            }]
        }]
    }],
    tooltipMinWidth: 20,
    decorClock: 'M' + [
                    [0, -6],
                    [6, 0],
                    [0, 6]
                ].map(l => l.join(' ')).join(' L')
});

export const BoxView = GateView.extend({
    _autoResizeBox: false,
    render() {
        //todo: resize Box after port label / IO name / mode / bit size change
        GateView.prototype.render.apply(this, arguments);
        if (this._autoResizeBox && !this.model.get('box_resized')) {
            this.model.set('box_resized', true);
            this.model.prop('size/width', this._calculateBoxWidth());
        }
    },
    _calculateBoxWidth() {
        const leftlabels = Array.from(this.el.querySelectorAll('[port-group=in] > text.iolabel'));
        const rightlabels = Array.from(this.el.querySelectorAll('[port-group=out] > text.iolabel'));
        const leftwidth = Math.max(...leftlabels.map(x => x.getBBox().width));
        const rightwidth = Math.max(...rightlabels.map(x => x.getBBox().width));
        const fixup = x => x == -Infinity ? -5 : x;
        const width = fixup(leftwidth) + fixup(rightwidth) + 25;
        return width;
    }
});

